import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  Card,
  CardContent,
} from "@arcadeai/design-system/components/ui/atoms/card";
import { cn } from "@arcadeai/design-system/lib/utils";
import { Building2, Mail, Plus } from "lucide-react";
import { useMemo } from "react";
import { useProjectPermissions } from "@/app/capabilities/api/hooks";
import { CanProject } from "@/app/capabilities/components/can-project";
import { PROJECT } from "@/app/capabilities/permissions";
import { DataTable } from "@/components/custom/table/data-table";
import { DataTableContent } from "@/components/custom/table/data-table-content";
import { DataTablePagination } from "@/components/custom/table/data-table-pagination";
import { useColumnVisibility } from "@/components/custom/table/hooks/use-column-visibility";
import {
  useDashboardOrgProject,
  useOrgProjectRouteContext,
} from "@/lib/org-project-context/hooks";
import { useGetProjectInvitations } from "../../api/hooks";
import { InviteMemberModal } from "../modals/invite-member-modal";
import { getProjectInvitationsColumns } from "./project-invitations-columns";
import { ProjectInvitationsToolbar } from "./project-invitations-toolbar";

type ProjectInvitationsDashboardProps = {
  className?: string;
  embedded?: boolean;
};

export function ProjectInvitationsDashboard({
  className,
  embedded = false,
}: ProjectInvitationsDashboardProps) {
  const { activeOrganization, activeProject } = useDashboardOrgProject();
  const { organizationId, projectId } = useOrgProjectRouteContext();
  const { data } = useGetProjectInvitations();

  const { canProject } = useProjectPermissions();
  const canDelete = canProject(PROJECT.invitations.delete);
  const canCreate = canProject(PROJECT.invitations.create);

  const allInvites = data?.items ?? [];
  // Role data is only present when operator-RBAC is enabled (legacy/flag-off omits it).
  const showRole = allInvites.some((invitation) => Boolean(invitation.role));
  const columns = useMemo(
    () => getProjectInvitationsColumns(showRole, canDelete),
    [showRole, canDelete]
  );
  const [columnVisibility, setColumnVisibility] = useColumnVisibility(
    columns,
    `project-invitations-columns-${organizationId}-${projectId}`
  );

  const totalPending = allInvites.length;

  return (
    <div className={cn("flex h-full flex-col", className)}>
      {/* Header */}
      {!embedded && (
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h2 className="font-semibold font-serif text-2xl tracking-tight">
              Project Invitations
            </h2>
            <p className="mt-1 text-muted-foreground text-sm">
              All pending invitations for this project
            </p>
          </div>
          <CanProject permission={PROJECT.invitations.create}>
            <InviteMemberModal
              trigger={
                <Button type="button">
                  <Plus className="mr-2 h-4 w-4" />
                  Invite Member
                </Button>
              }
            />
          </CanProject>
        </div>
      )}

      {/* Quick Stats */}
      {!embedded && (
        <div className="mb-4 grid grid-cols-2 gap-3">
          {/* Total Pending */}
          <div className="flex flex-col rounded-xl border bg-card/50 p-3">
            <div className="mb-1.5 flex items-center gap-1.5 text-muted-foreground text-xs">
              <Mail className="h-3.5 w-3.5 shrink-0" />
              <span>Total pending</span>
            </div>
            <p className="font-bold text-2xl tabular-nums leading-none">
              {totalPending}
            </p>
          </div>

          {/* Current Project */}
          <div className="rounded-xl border bg-card/50 p-3">
            <div className="mb-1.5 flex items-center gap-1.5 text-muted-foreground text-xs">
              <Building2 className="h-3.5 w-3.5 shrink-0" />
              <span>Current Project</span>
            </div>
            <div className="flex items-baseline gap-1.5 pt-0.5">
              <p className="font-semibold leading-tight">
                {activeProject?.name ?? "—"}
              </p>
              <span className="text-muted-foreground text-xs">•</span>
              <p className="text-muted-foreground text-sm leading-tight">
                {activeOrganization?.name ?? "—"}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Invitations Table */}
      {totalPending === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center py-6 text-center xl:py-12">
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-muted xl:mb-4 xl:h-12 xl:w-12">
              <Mail className="h-5 w-5 text-muted-foreground xl:h-6 xl:w-6" />
            </div>
            <h3 className="mb-1 font-semibold text-sm xl:text-base">
              No pending invitations
            </h3>
            <p className="mb-3 text-muted-foreground text-xs xl:text-sm">
              {canCreate
                ? "Invite teammates to start collaborating"
                : "No one has been invited to this project yet"}
            </p>
            <CanProject permission={PROJECT.invitations.create}>
              <InviteMemberModal
                trigger={
                  <Button size="sm" type="button" variant="outline">
                    <Plus className="mr-1.5 h-3.5 w-3.5" />
                    Invite Member
                  </Button>
                }
              />
            </CanProject>
          </CardContent>
        </Card>
      ) : (
        <DataTable
          className="space-y-4"
          columns={columns}
          columnVisibility={columnVisibility}
          data={allInvites}
          defaultPageSize={10}
          onColumnVisibilityChange={setColumnVisibility}
        >
          <ProjectInvitationsToolbar />
          <DataTableContent stickyColumnBg="card" />
          <DataTablePagination />
        </DataTable>
      )}
    </div>
  );
}
